import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { Briefcase, GraduationCap, Trophy, Phone, MessageCircle, TrendingUp, Star, Award, Sparkles, Flame, Crown, Zap, Users } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

const achieverCategories = [
  { id: "banking", label: "Banking",    accent: "from-blue-600 to-vedanti-navy" },
  { id: "ssc",     label: "SSC",        accent: "from-emerald-600 to-green-500" },
  { id: "railway", label: "Railway",    accent: "from-amber-600 to-vedanti-orange" },
  { id: "other",   label: "Other Govt", accent: "from-purple-600 to-pink-500" },
];

// Resolve achiever photo: "/api/..." → backend upload; static path / full url → as-is; null → initials
const resolvePhoto = (photo) => {
  if (!photo) return null;
  if (photo.startsWith("/api/")) return `${API}${photo}`;
  return photo;
};

const getInitials = (name) =>
  (name || "?").split(" ").map((w) => w[0]).filter(Boolean).slice(0, 2).join("").toUpperCase();

const govtJobResults = [
  { name: "Prathmesh Banote", achievement: "IBPS Clerk", organization: "Bank of India", photo: "/achievers/2.webp" },
  { name: "Sapna Shekhawat", achievement: "IBPS PO", organization: "Union Bank of India", photo: "/achievers/3.webp" },
  { name: "Aditya Mohite", achievement: "IBPS PO", organization: "Union Bank of India", photo: "/achievers/4.webp" },
  { name: "Sumit Jadhav", achievement: "ADO", organization: "LIC", photo: "/achievers/5.webp" },
  { name: "Prarthna Sawant", achievement: "IBPS Clerk", organization: "Bank of India", photo: "/achievers/6.webp" },
  { name: "Vilas Naik", achievement: "Attendant", organization: "Reserve Bank of India", photo: "/achievers/7.webp" },
  { name: "Aniket Gangurde", achievement: "Goods Guard", organization: "Indian Railways", photo: "/achievers/8.webp" },
  { name: "Kaiwalya Ghotkar", achievement: "IBPS Clerk", organization: "Central Bank of India", photo: "/achievers/9.webp" },
  { name: "Onkar Jadhav", achievement: "Executive", organization: "Bombay Mercantile Bank", photo: "/achievers/10.webp" },
  { name: "Priya Talreja", achievement: "SBI Clerk", organization: "State Bank of India", photo: "/achievers/11.webp" },
  { name: "Poonam Chaudhary", achievement: "Credit Manager", organization: "Bank of Maharashtra", photo: "/achievers/12.webp" },
  { name: "Heena Soneji", achievement: "Clerk", organization: "Saraswat Bank", photo: "/achievers/13.webp" },
  { name: "Shrushti Sahu", achievement: "Assistant", organization: "Food Corporation of India", photo: "/achievers/14.webp" },
  { name: "Roshni Sonawane", achievement: "Clerk", organization: "Saraswat Bank", photo: "/achievers/15.webp" },
  { name: "Devesh Pahade", achievement: "SBI PO", organization: "State Bank of India", photo: "/achievers/16.webp" },
  { name: "Manali Bhamare", achievement: "SBI Clerk", organization: "State Bank of India", photo: "/achievers/17.webp" },
  { name: "Pusthi Thakkar", achievement: "Executive", organization: "IDBI Bank", photo: "/achievers/18.webp" },
];

const mbaResults = [
  { name: "Abhishek Payas", exam: "CAT", percentile: "95", photo: "/achievers/20.webp" },
  { name: "Vishal Singh", exam: "MBA CET", percentile: "99", photo: "/achievers/21.webp" },
  { name: "Sakshi Jathar", exam: "MBA CET", percentile: "98.63", photo: "/achievers/22.webp" },
  { name: "Chaitali Pawar", exam: "MBA CET", percentile: "98.42", photo: "/achievers/23.webp" },
  { name: "Prerna Mehta", exam: "MBA CET", percentile: "98.28", photo: "/achievers/24.webp" },
  { name: "Suvel Lamture", exam: "MBA CET", percentile: "97.76", photo: "/achievers/25.webp" },
  { name: "Aditya Sharma", exam: "MBA CET", percentile: "97.53", photo: "/achievers/26.webp" },
  { name: "Sankruti Jaiswal", exam: "MBA CET", percentile: "97.28", photo: "/achievers/27.webp" },
  { name: "Rohan Shinde", exam: "MBA CET", percentile: "96", photo: "/achievers/28.webp" },
  { name: "Mayur Gunjal", exam: "MBA CET", percentile: "95.87", photo: "/achievers/29.webp" },
  { name: "Abhijeet Bhagat", exam: "MBA CET", percentile: "92.64", photo: "/achievers/30.webp" },
  { name: "Ayan Sheikh", exam: "MBA CET", percentile: "90", photo: "/achievers/31.webp" },
  { name: "Pallavi Shinde", exam: "MBA CET", percentile: "86", photo: "/achievers/32.webp" },
  { name: "Samiksha Tambe", exam: "MBA CET", percentile: "85", photo: "/achievers/33.webp" },
];

const govtAccentColors = [
  "from-blue-500 to-cyan-400",
  "from-vedanti-orange to-amber-400",
  "from-emerald-500 to-green-400",
  "from-purple-500 to-pink-400",
  "from-vedanti-navy to-blue-500",
  "from-vedanti-mango to-yellow-400",
  "from-rose-500 to-pink-400",
  "from-cyan-500 to-sky-400",
  "from-indigo-500 to-violet-400",
  "from-teal-500 to-emerald-400",
];

const govtBadgeColors = {
  "IBPS Clerk": "bg-gradient-to-r from-blue-600 to-sky-500",
  "IBPS PO": "bg-gradient-to-r from-vedanti-navy to-blue-600",
  "SBI Clerk": "bg-gradient-to-r from-sky-600 to-cyan-500",
  "SBI PO": "bg-gradient-to-r from-indigo-600 to-purple-500",
  "ADO": "bg-gradient-to-r from-green-600 to-emerald-500",
  "Attendant": "bg-gradient-to-r from-teal-600 to-cyan-500",
  "Goods Guard": "bg-gradient-to-r from-amber-600 to-orange-500",
  "Executive": "bg-gradient-to-r from-purple-600 to-pink-500",
  "Clerk": "bg-gradient-to-r from-vedanti-orange to-amber-500",
  "Credit Manager": "bg-gradient-to-r from-rose-600 to-pink-500",
  "Assistant": "bg-gradient-to-r from-emerald-600 to-green-500",
};

export default function GalleryPage() {
  const [activeCat, setActiveCat] = useState("all");
  const [achievers, setAchievers] = useState([]);

  useEffect(() => {
    let active = true;
    fetch(`${API}/api/achievers`)
      .then((r) => (r.ok ? r.json() : []))
      .then((data) => { if (active) setAchievers(Array.isArray(data) ? data : []); })
      .catch(() => { if (active) setAchievers([]); });
    return () => { active = false; };
  }, []);

  const visibleAchievers = activeCat === "all" ? achievers : achievers.filter((a) => a.category === activeCat);
  return (
    <div data-testid="gallery-page">
      <Helmet>
        <title>Our Achievers & Success Stories | Vedanti Academy</title>
        <meta name="description" content="Meet Vedanti Academy's achievers - students placed in SBI, IBPS, RBI, Indian Railways, LIC & MBA colleges. 99% MBA CET, 95% CAT toppers. See real student success stories." />
        <meta name="keywords" content="Vedanti Academy results, student achievers, IBPS selected students, SBI PO results, MBA CET toppers, coaching institute results Badlapur" />
        <meta property="og:title" content="Wall of Fame - Our Achievers | Vedanti Academy" />
        <meta property="og:description" content="Real student success stories from Vedanti Academy. Banking selections, MBA toppers & government job achievers." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://vedantiacademy.com/gallery" />
        <link rel="canonical" href="https://vedantiacademy.com/gallery" />
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "CollectionPage",
          "name": "Wall of Fame - Vedanti Academy Achievers",
          "description": "Meet Vedanti Academy's achievers placed in SBI, IBPS, RBI, Indian Railways, LIC and MBA colleges.",
          "url": "https://vedantiacademy.com/gallery",
          "mainEntity": {"@type": "ItemList", "numberOfItems": 56, "name": "Student Achievers"}
        })}</script>
      </Helmet>

      {/* Hero Header */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 bg-vedanti-navy"></div>
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,_rgba(249,115,22,0.25)_0%,_transparent_50%)]"></div>
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_bottom_left,_rgba(255,195,0,0.15)_0%,_transparent_50%)]"></div>
        <div className="absolute top-8 right-16 w-72 h-72 bg-vedanti-orange/10 rounded-full blur-3xl animate-pulse"></div>
        <div className="absolute bottom-4 left-10 w-48 h-48 bg-vedanti-mango/10 rounded-full blur-3xl animate-pulse" style={{ animationDelay: "1s" }}></div>
        <div className="absolute bottom-0 left-0 right-0 h-1.5 bg-gradient-to-r from-vedanti-orange via-vedanti-mango to-vedanti-orange"></div>
        <div className="container-main py-16 md:py-24 relative z-10">
          <div className="flex items-center gap-3 mb-5">
            <div className="w-14 h-14 bg-gradient-to-br from-vedanti-orange to-vedanti-mango rounded-2xl flex items-center justify-center shadow-lg shadow-vedanti-orange/30">
              <Trophy size={28} className="text-white" />
            </div>
            <span className="text-vedanti-mango font-bold text-sm uppercase tracking-[0.2em]">Wall of Fame</span>
          </div>
          <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-extrabold text-white leading-tight">
            Our <span className="text-transparent bg-clip-text bg-gradient-to-r from-vedanti-orange via-vedanti-mango to-amber-300">Achievers</span>
          </h1>
          <p className="text-slate-300 mt-5 text-lg max-w-2xl leading-relaxed">Proud moments from Vedanti Academy — students who turned their dreams into reality through dedication and our guidance</p>
        </div>
      </section>

      {/* Colorful highlights ribbon */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-vedanti-navy via-[#001a3a] to-vedanti-navy"></div>
        <div className="container-main relative z-10 py-5">
          <div className="flex flex-wrap justify-center gap-3 md:gap-5">
            {[
              { icon: Briefcase, label: "Govt Job Selections", bg: "bg-gradient-to-r from-blue-600 to-cyan-500" },
              { icon: GraduationCap, label: "MBA Toppers", bg: "bg-gradient-to-r from-vedanti-orange to-amber-500" },
              { icon: Star, label: "99% Highest CET", bg: "bg-gradient-to-r from-vedanti-mango to-yellow-400" },
              { icon: Award, label: "95% Highest CAT", bg: "bg-gradient-to-r from-emerald-500 to-green-400" },
              { icon: Sparkles, label: "500+ Google Reviews", bg: "bg-gradient-to-r from-pink-500 to-rose-400" },
            ].map((item) => {
              const Icon = item.icon;
              return (
                <div key={item.label} className={`flex items-center gap-2 ${item.bg} rounded-full px-4 py-2 shadow-lg`}>
                  <Icon size={14} className="text-white" />
                  <span className="text-white text-xs font-bold tracking-wide">{item.label}</span>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ====== GOVT JOB RESULTS ====== */}
      <section className="relative overflow-hidden py-16 md:py-20" data-testid="govt-job-results">
        <div className="absolute inset-0 bg-gradient-to-br from-slate-50 via-blue-50/40 to-indigo-50/30"></div>
        <div className="absolute top-0 right-0 w-96 h-96 bg-blue-200/20 rounded-full blur-3xl"></div>
        <div className="absolute bottom-0 left-0 w-80 h-80 bg-vedanti-orange/5 rounded-full blur-3xl"></div>

        <div className="container-main relative z-10">
          <div className="text-center mb-14">
            <div className="inline-block relative mb-5">
              <div className="absolute -inset-1 bg-gradient-to-r from-blue-600 via-vedanti-navy to-indigo-600 rounded-2xl blur-sm opacity-70"></div>
              <div className="relative bg-vedanti-navy text-white font-bold text-sm uppercase tracking-[0.15em] px-8 py-3 rounded-2xl flex items-center gap-2.5 shadow-xl">
                <Briefcase size={16} /> Government Job Selections
              </div>
            </div>
            <h2 className="font-heading text-3xl md:text-5xl font-extrabold text-vedanti-navy mt-2">
              Banking, Railway & <span className="text-transparent bg-clip-text bg-gradient-to-r from-vedanti-orange to-red-500">Govt Achievers</span>
            </h2>
            <p className="text-slate-500 mt-3 max-w-2xl mx-auto text-base md:text-lg">Our students are now proudly serving in top government banks, railways, and organizations across India</p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {govtJobResults.map((student, idx) => {
              const accent = govtAccentColors[idx % govtAccentColors.length];
              const badge = govtBadgeColors[student.achievement] || "bg-gradient-to-r from-vedanti-navy to-blue-600";
              return (
                <div
                  key={student.name}
                  className="bg-white rounded-2xl shadow-md hover:shadow-2xl transition-all duration-300 overflow-hidden group hover:-translate-y-1"
                  data-testid={`govt-result-${idx}`}
                >
                  {/* Colored top accent bar */}
                  <div className={`h-1.5 bg-gradient-to-r ${accent}`}></div>

                  {/* Photo area */}
                  <div className="relative overflow-hidden bg-slate-100">
                    <img
                      src={student.photo}
                      alt={`${student.name} - ${student.achievement} at ${student.organization}`}
                      className="w-full aspect-square object-cover object-top group-hover:scale-105 transition-transform duration-700"
                      loading="lazy"
                    />
                    {/* Dark gradient at bottom */}
                    <div className="absolute bottom-0 left-0 right-0 h-28 bg-gradient-to-t from-black/70 via-black/30 to-transparent"></div>
                    {/* Achievement badge top-right */}
                    <div className={`absolute top-3 right-3 ${badge} text-white text-xs font-bold px-3.5 py-1.5 rounded-full shadow-lg`}>
                      {student.achievement}
                    </div>
                    {/* Name on photo bottom */}
                    <div className="absolute bottom-3 left-4 right-4">
                      <h3 className="font-heading font-bold text-white text-lg drop-shadow-lg leading-tight">{student.name}</h3>
                    </div>
                  </div>

                  {/* Info below */}
                  <div className="px-4 py-3.5 bg-gradient-to-r from-slate-50 to-white">
                    <div className="flex items-center gap-2.5">
                      <div className={`w-8 h-8 rounded-lg bg-gradient-to-br ${accent} flex items-center justify-center flex-shrink-0`}>
                        <Briefcase size={14} className="text-white" />
                      </div>
                      <span className="text-slate-700 font-semibold text-sm leading-tight">{student.organization}</span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Colorful divider */}
      <div className="relative h-4 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-vedanti-orange via-vedanti-mango via-50% to-vedanti-orange"></div>
        <div className="absolute inset-0 bg-[repeating-linear-gradient(90deg,transparent,transparent_40px,rgba(255,255,255,0.15)_40px,rgba(255,255,255,0.15)_80px)]"></div>
      </div>

      {/* ====== MBA RESULTS ====== */}
      <section className="relative overflow-hidden py-16 md:py-20" data-testid="mba-results">
        <div className="absolute inset-0 bg-gradient-to-br from-amber-50/60 via-orange-50/30 to-rose-50/20"></div>
        <div className="absolute top-0 left-0 w-96 h-96 bg-vedanti-mango/10 rounded-full blur-3xl"></div>
        <div className="absolute bottom-0 right-0 w-80 h-80 bg-rose-200/10 rounded-full blur-3xl"></div>

        <div className="container-main relative z-10">
          <div className="text-center mb-14">
            <div className="inline-block relative mb-5">
              <div className="absolute -inset-1 bg-gradient-to-r from-vedanti-orange via-amber-500 to-red-500 rounded-2xl blur-sm opacity-70"></div>
              <div className="relative bg-gradient-to-r from-vedanti-orange to-amber-500 text-white font-bold text-sm uppercase tracking-[0.15em] px-8 py-3 rounded-2xl flex items-center gap-2.5 shadow-xl">
                <GraduationCap size={16} /> MBA Entrance Results
              </div>
            </div>
            <h2 className="font-heading text-3xl md:text-5xl font-extrabold text-vedanti-navy mt-2">
              MBA CET & CAT <span className="text-transparent bg-clip-text bg-gradient-to-r from-vedanti-orange via-red-500 to-rose-500">Toppers</span>
            </h2>
            <p className="text-slate-500 mt-3 max-w-2xl mx-auto text-base md:text-lg">Our students consistently dominate MBA entrance exams with outstanding percentiles</p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {mbaResults.map((student, idx) => {
              const pct = parseFloat(student.percentile);
              const ringColor = pct >= 98 ? "from-vedanti-orange to-red-500" : pct >= 95 ? "from-amber-500 to-vedanti-orange" : pct >= 90 ? "from-vedanti-mango to-amber-500" : "from-green-500 to-emerald-400";
              return (
                <div
                  key={student.name}
                  className="bg-white rounded-2xl shadow-md hover:shadow-2xl transition-all duration-300 overflow-hidden group hover:-translate-y-1"
                  data-testid={`mba-result-${idx}`}
                >
                  {/* Colored top accent bar */}
                  <div className={`h-1.5 bg-gradient-to-r ${ringColor}`}></div>

                  {/* Photo area */}
                  <div className="relative overflow-hidden bg-slate-100">
                    <img
                      src={student.photo}
                      alt={`${student.name} - ${student.exam} ${student.percentile} Percentile`}
                      className="w-full aspect-square object-cover object-top group-hover:scale-105 transition-transform duration-700"
                      loading="lazy"
                    />
                    {/* Dark gradient at bottom */}
                    <div className="absolute bottom-0 left-0 right-0 h-28 bg-gradient-to-t from-black/70 via-black/30 to-transparent"></div>
                    {/* Percentile badge top-left */}
                    <div className={`absolute top-3 left-3 bg-gradient-to-r ${ringColor} text-white font-extrabold text-sm px-3.5 py-1.5 rounded-full shadow-lg`}>
                      {student.percentile}%
                    </div>
                    {/* Exam badge top-right */}
                    <div className="absolute top-3 right-3 bg-white/95 text-vedanti-navy text-xs font-bold px-3 py-1.5 rounded-full shadow-md backdrop-blur-sm">
                      {student.exam}
                    </div>
                    {/* Name on photo bottom */}
                    <div className="absolute bottom-3 left-4 right-4">
                      <h3 className="font-heading font-bold text-white text-lg drop-shadow-lg leading-tight">{student.name}</h3>
                    </div>
                  </div>

                  {/* Info below */}
                  <div className="px-4 py-3.5 bg-gradient-to-r from-slate-50 to-white">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <div className={`w-8 h-8 rounded-lg bg-gradient-to-br ${ringColor} flex items-center justify-center`}>
                          <TrendingUp size={14} className="text-white" />
                        </div>
                        <span className="text-slate-600 font-medium text-sm">{student.exam}</span>
                      </div>
                      <span className={`font-heading font-extrabold text-2xl text-transparent bg-clip-text bg-gradient-to-r ${ringColor}`}>{student.percentile}%</span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ====== INDIVIDUAL ACHIEVER PHOTOS ====== */}
      <section className="relative overflow-hidden py-16 md:py-20" data-testid="single-achievers-section">
        <div className="absolute inset-0 bg-gradient-to-br from-slate-50 via-indigo-50/30 to-blue-50/20"></div>
        <div className="absolute top-10 left-0 w-80 h-80 bg-indigo-200/15 rounded-full blur-3xl"></div>
        <div className="absolute bottom-10 right-0 w-96 h-96 bg-vedanti-orange/5 rounded-full blur-3xl"></div>

        <div className="container-main relative z-10">
          <div className="text-center mb-10">
            <div className="inline-block relative mb-5">
              <div className="absolute -inset-1 bg-gradient-to-r from-indigo-600 via-vedanti-navy to-blue-600 rounded-2xl blur-sm opacity-60"></div>
              <div className="relative bg-gradient-to-r from-indigo-700 to-vedanti-navy text-white font-bold text-sm uppercase tracking-[0.15em] px-8 py-3 rounded-2xl flex items-center gap-2.5 shadow-xl">
                <Users size={16} /> Individual Selections
              </div>
            </div>
            <h2 className="font-heading text-3xl md:text-5xl font-extrabold text-vedanti-navy mt-2">
              Recent <span className="text-transparent bg-clip-text bg-gradient-to-r from-indigo-600 to-vedanti-navy">Selections</span>
            </h2>
            <p className="text-slate-500 mt-3 max-w-2xl mx-auto text-base md:text-lg">
              Real selections from Vedanti Academy — students placed in Banking, SSC, Railway, Insurance and other government services.
            </p>
          </div>

          {/* Category Tabs */}
          <div className="flex flex-wrap justify-center gap-2 md:gap-3 mb-10" data-testid="single-achievers-tabs">
            <button
              type="button"
              onClick={() => setActiveCat("all")}
              className={`px-5 py-2 rounded-full text-sm font-bold transition-all ${activeCat === "all" ? "bg-vedanti-navy text-white shadow-lg" : "bg-white text-slate-600 hover:text-vedanti-navy border border-slate-200"}`}
              data-testid="single-achievers-tab-all"
            >
              All <span className="ml-1 opacity-70">({achievers.length})</span>
            </button>
            {achieverCategories.map((cat) => {
              const count = achievers.filter((a) => a.category === cat.id).length;
              const isActive = activeCat === cat.id;
              return (
                <button
                  key={cat.id}
                  type="button"
                  onClick={() => setActiveCat(cat.id)}
                  className={`px-5 py-2 rounded-full text-sm font-bold transition-all ${isActive ? `bg-gradient-to-r ${cat.accent} text-white shadow-lg` : "bg-white text-slate-600 hover:text-vedanti-navy border border-slate-200"}`}
                  data-testid={`single-achievers-tab-${cat.id}`}
                >
                  {cat.label} <span className="ml-1 opacity-70">({count})</span>
                </button>
              );
            })}
          </div>

          {/* Achievers Grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {visibleAchievers.map((a) => {
              const cat = achieverCategories.find((c) => c.id === a.category) || achieverCategories[0];
              const src = resolvePhoto(a.photo);
              return (
                <div
                  key={a.id}
                  className="bg-white rounded-2xl shadow-md hover:shadow-2xl transition-all duration-300 overflow-hidden group hover:-translate-y-1"
                  data-testid={`single-achiever-${a.id}`}
                >
                  {/* Colored top accent bar */}
                  <div className={`h-1.5 bg-gradient-to-r ${cat.accent}`}></div>

                  {/* Photo area — square, top-anchored crop; initials fallback when no photo */}
                  <div className="relative overflow-hidden bg-slate-100">
                    {src ? (
                      <img
                        src={src}
                        alt={`${a.name} — ${a.role} at ${a.organization}, Vedanti Academy achiever`}
                        className="w-full aspect-square object-cover object-top group-hover:scale-105 transition-transform duration-700"
                        loading="lazy"
                      />
                    ) : (
                      <div className={`w-full aspect-square flex items-center justify-center bg-gradient-to-br ${cat.accent}`}>
                        <span className="font-heading font-extrabold text-white text-5xl drop-shadow">{getInitials(a.name)}</span>
                      </div>
                    )}
                    {/* Dark gradient at bottom for name readability */}
                    <div className="absolute bottom-0 left-0 right-0 h-28 bg-gradient-to-t from-black/70 via-black/30 to-transparent"></div>
                    {/* Role badge top-right */}
                    <div className={`absolute top-3 right-3 bg-gradient-to-r ${cat.accent} text-white text-xs font-bold px-3.5 py-1.5 rounded-full shadow-lg`}>
                      {a.role}
                    </div>
                    {/* Name overlay bottom */}
                    <div className="absolute bottom-3 left-4 right-4">
                      <h3 className="font-heading font-bold text-white text-lg drop-shadow-lg leading-tight">{a.name}</h3>
                    </div>
                  </div>

                  {/* Info bar below */}
                  <div className="px-4 py-3.5 bg-gradient-to-r from-slate-50 to-white">
                    <div className="flex items-center gap-2.5">
                      <div className={`w-8 h-8 rounded-lg bg-gradient-to-br ${cat.accent} flex items-center justify-center flex-shrink-0`}>
                        <Briefcase size={14} className="text-white" />
                      </div>
                      <span className="text-slate-700 font-semibold text-sm leading-tight">{a.organization}</span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {visibleAchievers.length === 0 && (
            <p className="text-center text-slate-500 py-12">No achievers in this category yet.</p>
          )}
        </div>
      </section>

      {/* CTA */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-vedanti-orange via-amber-500 to-vedanti-mango"></div>
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_left,_rgba(255,255,255,0.2)_0%,_transparent_50%)]"></div>
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_bottom_right,_rgba(0,33,71,0.15)_0%,_transparent_50%)]"></div>
        <div className="absolute top-0 left-0 right-0 h-4 overflow-hidden">
          <div className="h-full bg-gradient-to-r from-vedanti-navy via-blue-800 to-vedanti-navy"></div>
        </div>
        <div className="container-main py-16 md:py-20 relative z-10 text-center">
          <div className="inline-flex items-center justify-center w-20 h-20 bg-white/20 backdrop-blur-sm rounded-3xl mb-6 shadow-xl">
            <Zap size={36} className="text-white" />
          </div>
          <h2 className="font-heading text-3xl md:text-5xl font-extrabold text-white mb-4">Be the Next Achiever!</h2>
          <p className="text-white/90 mb-10 max-w-xl mx-auto text-lg">Join Vedanti Academy and start your journey towards a successful career in government or management</p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link
              to="/demo-class"
              className="bg-white text-vedanti-navy hover:bg-vedanti-navy hover:text-white rounded-xl px-8 py-4 font-bold text-lg shadow-xl hover:-translate-y-1 transition-all duration-300"
              data-testid="gallery-demo-btn"
            >
              Book Free Demo Class
            </Link>
            <a
              href="https://wa.me/918308776505?text=Hi%2C%20I%20want%20to%20join%20Vedanti%20Academy."
              target="_blank"
              rel="noopener noreferrer nofollow"
              className="border-2 border-white text-white hover:bg-white hover:text-green-600 rounded-xl px-8 py-4 font-semibold transition-all duration-300 inline-flex items-center gap-2 hover:-translate-y-1"
              data-testid="gallery-whatsapp-btn"
            >
              <MessageCircle size={20} /> WhatsApp Us
            </a>
            <a
              href="tel:+918308776505"
              className="border-2 border-white text-white hover:bg-white hover:text-vedanti-orange rounded-xl px-8 py-4 font-semibold transition-all duration-300 inline-flex items-center gap-2 hover:-translate-y-1"
              data-testid="gallery-call-btn"
            >
              <Phone size={20} /> Call Now
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}
